import React, { useState } from "react";

import { getDestinationLabel } from "@/admin/useDestinationLabel";
import { updateConsentManagementConfigVersion } from "@/consent-management/api/updateConsentManagementConfigVersion";
import { Banner } from "@/design-system/components/Banner/Banner";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Logo } from "@/design-system/components/Logo/Logo";
import { Select } from "@/design-system/components/Select/Select";
import { Typography } from "@/design-system/components/Typography/Typography";
import { SaaSDestinationSlug } from "@/destinations/types";
import { unpackRequestErrorMessage } from "@/utils/request";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { ConsentLoadError } from "../ConsentLoadError";
import { ESSENTIAL_ALWAYS_SENT, versionBehavior } from "../copy";
import {
  AUDIENCES_MAPPING_KEY,
  suggestedAudiencesCategory,
} from "../draftStatus";
import { ConsentManagerStepProps } from "../types";
import {
  freshpaintConsent,
  LOAD_ERROR_REASON,
  NO_WRITE_ACCESS_REASON,
  useFreshpaintConsent,
} from "../useFreshpaintConsent";

type Row = {
  id: string;
  mappingKey: string;
  name: string;
  /** Tells apart several instances of one destination. */
  instanceLabel?: string;
  description?: string;
  logo?: SaaSDestinationSlug;
};

export function CategorizeStep({
  data,
  updateData,
  goNext,
}: ConsentManagerStepProps) {
  const {
    draft,
    destinations,
    canWrite,
    isLoading,
    loadError,
    retry,
    audiencesEnabled,
    fetchLatestDraft,
  } = useFreshpaintConsent();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows: Row[] = [
    ...destinations.map(({ destinationConfig, saasDestination }) => ({
      id: destinationConfig.id,
      mappingKey: destinationConfig.name,
      name: saasDestination.name,
      instanceLabel: getDestinationLabel(destinationConfig, saasDestination),
      logo: saasDestination.slug as SaaSDestinationSlug,
    })),
    ...(audiencesEnabled
      ? [
          {
            id: AUDIENCES_MAPPING_KEY,
            mappingKey: AUDIENCES_MAPPING_KEY,
            name: "Audience destinations",
            description:
              "Visitors who decline this category are left out of audiences built from Freshpaint’s events.",
          },
        ]
      : []),
  ];

  const suggestedFor = (
    mappingKey: string,
    categoryMapping: Record<string, string>,
  ) =>
    mappingKey === AUDIENCES_MAPPING_KEY
      ? suggestedAudiencesCategory(categoryMapping)
      : "";
  const categoryFor = (mappingKey: string) =>
    data.categoryDraft[mappingKey] ||
    draft?.consentMapping[mappingKey] ||
    suggestedFor(mappingKey, draft?.categoryMapping ?? {});
  const uncategorized = rows.filter(
    ({ mappingKey }) => !categoryFor(mappingKey),
  );

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const latest = await fetchLatestDraft();
      if (!latest) {
        throw new Error("Your consent banner draft couldn’t be found");
      }
      // Only this session's picks are sent, so a category changed on the
      // Consent Manager page since the wizard loaded isn't overwritten.
      const picks: Record<string, string> = { ...data.categoryDraft };
      for (const { mappingKey } of rows) {
        const suggested = suggestedFor(mappingKey, latest.categoryMapping);
        if (
          suggested &&
          !picks[mappingKey] &&
          !latest.consentMapping[mappingKey]
        ) {
          picks[mappingKey] = suggested;
        }
      }
      if (
        Object.entries(picks).some(
          ([key, category]) => latest.consentMapping[key] !== category,
        )
      ) {
        await updateConsentManagementConfigVersion({
          type: freshpaintConsent.slug,
          consentMapping: { ...latest.consentMapping, ...picks },
        });
      }
      updateData({ categoryDraft: {} });
      goNext();
    } catch (saveError) {
      setError(unpackRequestErrorMessage(saveError));
      setSaving(false);
    }
  }

  return (
    <WizardStepLayout
      title="Categorize your destinations"
      description={
        draft
          ? `${versionBehavior(draft)} ${ESSENTIAL_ALWAYS_SENT} We’ve suggested a category for each one.`
          : undefined
      }
      primaryAction={{
        label: "Save and continue",
        onClick: save,
        loading: saving,
        disabled:
          isLoading ||
          loadError ||
          !draft ||
          uncategorized.length > 0 ||
          canWrite === false,
        disabledReason: loadError
          ? LOAD_ERROR_REASON
          : isLoading
            ? undefined
            : canWrite === false
              ? NO_WRITE_ACCESS_REASON
              : "Choose a category for every destination",
      }}
    >
      {loadError ? (
        <ConsentLoadError retry={retry} />
      ) : isLoading ? (
        <LoadingSpinner className="size-6" />
      ) : rows.length === 0 ? (
        <Typography variant="body-md" color="subtle">
          No destinations are turned on yet. You’ll pick a category for each one
          as you set it up.
        </Typography>
      ) : (
        <ul className="border-borderSubtle divide-borderSubtle divide-y rounded-lg border">
          {rows.map(
            ({ id, mappingKey, name, instanceLabel, description, logo }) => (
              <li key={id} className="flex items-center gap-3 px-4 py-2">
                {logo && <Logo brand={logo} className="size-6 shrink-0" />}
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-label-sm text-textPrimary truncate">
                    {name}
                  </span>
                  {(instanceLabel || description) && (
                    <span className="text-body-sm text-textTertiary">
                      {instanceLabel || description}
                    </span>
                  )}
                </div>
                <div className="w-56 shrink-0">
                  <Select
                    options={Object.entries(draft?.categoryMapping ?? {})}
                    getOptionLabel={([, label]) => label}
                    getOptionValue={([categoryId]) => categoryId}
                    rootProps={{
                      value: categoryFor(mappingKey),
                      onValueChange: (category) =>
                        updateData((current) => ({
                          categoryDraft: {
                            ...current.categoryDraft,
                            [mappingKey]: category,
                          },
                        })),
                      disabled: canWrite === false,
                    }}
                    triggerProps={{
                      className: "w-full px-3 py-2",
                      placeholder: "Select a category",
                      "aria-label": `Consent category for ${instanceLabel ? `${name} (${instanceLabel})` : name}`,
                    }}
                  />
                </div>
              </li>
            ),
          )}
        </ul>
      )}
      {error && <Banner type="error">{error}</Banner>}
    </WizardStepLayout>
  );
}
